import React, { useState, useEffect } from "react";
import { motion } from "motion/react";
import { Clock, Calendar, FileText, Sparkles, Layers } from "lucide-react";
import ExpertNotesForm from "../ui/ExpertNotesForm";
import PrepBriefView from "../ui/PrepBriefView";
import SessionDeckBuilder from "../ui/SessionDeckBuilder";

export default function ExpertConsultationsTab({
  selectedBooking,
  setSelectedBooking,
  upcomingBookings,
  completedBookings,
  expertBookings,
  completeBooking,
  expertProfile
}) {
  const [prepMode, setPrepMode] = useState(null);

  useEffect(() => {
    if (selectedBooking && window.__expertPrepMode === "prep") {
      setPrepMode("prep");
      delete window.__expertPrepMode;
    }
  }, [selectedBooking]);

  const handleOpenPrep = (booking) => {
    setSelectedBooking(booking);
    setPrepMode("prep");
  };

  const handleOpenDeck = (booking) => {
    setSelectedBooking(booking);
    setPrepMode("deck");
  };

  const handleBack = () => {
    setSelectedBooking(null);
    setPrepMode(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <h2 className="text-2xl font-medium text-white">Консультации</h2>

      {selectedBooking && prepMode === "prep" ? (
        <div className="space-y-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-neutral-400 hover:text-white transition-colors"
          >
            <span className="text-lg font-extralight">← Назад к списку</span>
          </button>
          <PrepBriefView
            booking={selectedBooking}
            expertProfile={expertProfile}
          />
        </div>
      ) : selectedBooking && prepMode === "deck" ? (
        <div className="space-y-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-neutral-400 hover:text-white transition-colors"
          >
            <span className="text-lg font-extralight">← Назад к списку</span>
          </button>
          <SessionDeckBuilder
            booking={selectedBooking}
            expertId={selectedBooking.expertId}
            onClose={handleBack}
            onSaved={() => {}}
          />
        </div>
      ) : selectedBooking && !prepMode ? (
        <div className="space-y-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-neutral-400 hover:text-white transition-colors"
          >
            <span className="text-lg font-extralight">← Назад к списку</span>
          </button>
          <ExpertNotesForm
            booking={selectedBooking}
            onClose={handleBack}
            onSaved={handleBack}
          />
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="p-5 rounded-2xl bg-neutral-900 border border-neutral-800">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-yellow-400" />
                <span className="text-lg font-extralight text-neutral-400">Запланировано</span>
              </div>
              <p className="text-2xl font-medium text-white">{upcomingBookings.length}</p>
            </div>
            <div className="p-5 rounded-2xl bg-neutral-900 border border-neutral-800">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-5 h-5 text-green-400" />
                <span className="text-lg font-extralight text-neutral-400">Завершено</span>
              </div>
              <p className="text-2xl font-medium text-white">{completedBookings.length}</p>
            </div>
          </div>

          {upcomingBookings.length > 0 && (
            <div className="p-6 rounded-2xl bg-neutral-900 border border-neutral-800">
              <h3 className="text-xl font-medium text-white mb-4">Предстоящие</h3>
              <div className="space-y-4">
                {upcomingBookings.map((b) => (
                  <div key={b.id} className="flex items-center gap-4 p-4 rounded-xl bg-neutral-800/50">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500/50 to-accent-500/50 flex items-center justify-center">
                      <span className="text-white font-medium">{b.clientName?.[0] || "K"}</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-medium text-white">{b.clientName || "Клиент"}</h4>
                      <p className="text-lg font-extralight text-neutral-400">{b.topic || "Без темы"}</p>
                    </div>
                    <div className="text-right mr-2">
                      <p className="text-lg font-medium text-white">
                        {new Date(b.date).toLocaleDateString("ru-RU")}
                      </p>
                      <span className="text-lg font-extralight text-yellow-400">{b.time}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleOpenPrep(b)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary-500/10 text-primary-400 hover:bg-primary-500/20 text-lg font-extralight transition-colors"
                        title="AI-подготовка"
                      >
                        <Sparkles className="w-4 h-4" />
                        Подготовиться
                      </button>
                      <button
                        onClick={() => completeBooking(b.id)}
                        className="px-3 py-1.5 rounded-md bg-green-500/10 text-green-400 hover:bg-green-500/20 text-lg font-extralight transition-colors"
                      >
                        Завершить
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {completedBookings.length > 0 && (
            <div className="p-6 rounded-2xl bg-neutral-900 border border-neutral-800">
              <h3 className="text-xl font-medium text-white mb-4">Завершенные</h3>
              <div className="space-y-4">
                {completedBookings.map((b) => (
                  <div key={b.id} className="flex items-center gap-4 p-4 rounded-xl bg-neutral-800/50">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500/50 to-accent-500/50 flex items-center justify-center">
                      <span className="text-white font-medium">{b.clientName?.[0] || "K"}</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-medium text-white">{b.clientName || "Клиент"}</h4>
                      <p className="text-lg font-extralight text-neutral-400">{b.topic || "Без темы"}</p>
                    </div>
                    <div className="text-right mr-2">
                      <p className="text-lg font-medium text-white">
                        {new Date(b.date).toLocaleDateString("ru-RU")}
                      </p>
                      <span className="text-lg font-extralight text-green-400">Завершено</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleOpenDeck(b)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent-500/10 text-accent-400 hover:bg-accent-500/20 text-lg font-extralight transition-colors"
                        title="Session Deck"
                      >
                        <Layers className="w-4 h-4" />
                        Материалы
                      </button>
                      <button
                        onClick={() => { setSelectedBooking(b); setPrepMode(null); }}
                        className="p-2 rounded-md hover:bg-neutral-700 text-neutral-500 hover:text-primary-400 transition-colors"
                        title="Заполнить заметки"
                      >
                        <FileText className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {expertBookings.length === 0 && (
            <div className="text-center py-8">
              <Calendar className="w-10 h-10 text-neutral-700 mx-auto mb-3" />
              <p className="text-lg font-extralight text-neutral-500">Пока нет консультаций</p>
            </div>
          )}
        </>
      )}
    </motion.div>
  );
}